import { Children, ReactElement, cloneElement, useState } from 'react';

import { Box, FloraTypography } from '@grupoboticario/flora-react';
import { theme } from 'ui/theme';

import { useCarousel } from './Carousel.hooks';
import Styles from './Carousel.styles';
import { CarouselModal } from './CarouselModal';
import { Navigation, Pagination, Stage } from './components';
import Thumbnail from './Thumbnail';

import type { CarouselProps, SelectableState } from './Carousel.d';

const Carousel = ({
  autoplay = false,
  children,
  container = true,
  desktop = false,
  dots = false,
  duration = 10000,
  infinite = false,
  itemsPerPage = {
    desktop: 1,
    mobile: 1,
  },
  fullContent = false,
  navigation = true,
  navigationSize,
  selectable = false,
  selectedItemIndex,
  dataTestid = 'carousel',
  gaEvents = false,
  tagStock = false,
  thumbnail = [],
  enlargeImgs = [],
  name = '',
  ...props
}: CarouselProps & SelectableState) => {
  const [openImg, setOpenImg] = useState(false);
  const currentItemsPerPage = desktop ? itemsPerPage.desktop : itemsPerPage.mobile;
  const whichNavigationSize = thumbnail.length > 0 ? 'lg' : navigationSize;
  const { emblaApi, emblaRef, scrollNext, scrollPrev, scrollTo, state, toggleSelected } =
    useCarousel({
      autoplay,
      container,
      desktop,
      dots,
      duration,
      itemsPerPage: currentItemsPerPage,
      infinite,
      navigationSize: whichNavigationSize ?? (desktop ? 'lg' : 'sm'),
      selectedItemIndex,
      selectable,
      gaEvents,
    });

  const closeModal = () => {
    setOpenImg(false);
  };

  const openModal = () => {
    setOpenImg(true);
  };
  const controlList = {
    scrollPrev,
    scrollNext,
    scrollTo,
    toggleSelected,
    openModal,
  };
  const { NextButton, PreviousButton } = Navigation({
    controls: controlList,
    state,
    gaEvents,
  });

  const hasMoreThanOnePage = emblaApi ? emblaApi.scrollSnapList().length > 1 : false;

  return (
    <Styles.Wrapper data-thumbnail={thumbnail.length > 0}>
      <Thumbnail
        openModal={openModal}
        thumbnail={thumbnail}
        controlList={controlList}
        state={state}
      />

      <Styles.CarouselImgContent data-thumbnail={thumbnail.length > 0}>
        <Styles.Component data-testid={dataTestid} data-container={container} {...props}>
          {navigation && hasMoreThanOnePage && PreviousButton}

          <Styles.Carousel ref={emblaRef} data-container={container} data-fullcontent={fullContent}>
            <Stage controls={controlList} state={state}>
              {Children.map(children, (child, index) => {
                const item = child as ReactElement;

                return cloneElement(item, { state, controls: controlList, index });
              })}
            </Stage>
          </Styles.Carousel>

          {navigation && hasMoreThanOnePage && NextButton}
        </Styles.Component>

        {tagStock && (
          <Box
            css={{
              backgroundColor: '$backgroundAltPrimary',
              display: 'flex',
              justifyContent: 'center',
              padding: '$3 0',
              position: 'absolute',
              bottom: '0',
              width: '100%',
              left: '50%',
              transform: 'translate(-50%, -100%)',
              opacity: '90%',
            }}
          >
            <FloraTypography
              fontSize="bodySmallStandard"
              css={{
                color: `${theme.colors.build.lightDark2}`,
              }}
            >
              Sem estoque
            </FloraTypography>
          </Box>
        )}

        {dots && hasMoreThanOnePage && <Pagination controls={controlList} state={state} />}
      </Styles.CarouselImgContent>
      {enlargeImgs.length > 0 && (
        <CarouselModal
          imgs={enlargeImgs}
          open={openImg}
          closeModal={closeModal}
          selectedImageIndex={state?.page || 0}
          name={name}
        />
      )}
    </Styles.Wrapper>
  );
};

export default Carousel;
